import styled, { useTheme } from 'styled-components/macro';
import useLocale from '../Hooks/use-locale';
import { Row } from '../SubComponents/Modals';
import { Heading2, Label, Link, SubLabel, Text } from '../SubComponents/Text';
import { Button } from '../SubComponents/Buttons';
import useFormatPrice from '../Hooks/use-format-price';
import useTypeSafeLocalize from '../Hooks/use-type-safe-localize';
import {
  LocalizationCategory,
  PriceLocalization,
  ProductLocalization,
} from '../localizations/types';

const Box = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${(props) => props.theme.colors.cancellationWarranty.backgroundColor};
  border-radius: 8px;
  padding: 24px;
  scroll-margin: 80px;
  margin-bottom: 32px;

  @media screen and (min-width: 768px) {
    max-width: 500px;
    margin: 0 auto;
    margin-bottom: 32px;
  }
`;

const VStack = styled(Row)`
  flex-direction: column;
`;

const HStack = styled(Row)``;

const CPButton = styled(Button)<{ selected: boolean; loading: boolean }>`
  ${(props) =>
    !props.loading && props.selected
      ? `background-color: ${props.theme.highLightColor} !important;`
      : null};
  ${(props) =>
    !props.loading && props.selected
      ? `color: ${props.theme.textColor} !important;`
      : null}

  width: 100%;

  @media screen and (min-width: 768px) {
    width: 50%;
  }
`;

type protectionHelpText = {
  lang?: string | null;
  value?: string | null;
};

type CancellationProtectionOptions = {
  warrantyHelpTextHeading?: Array<protectionHelpText | null> | null;
  warrantyHelpTextBody?: Array<protectionHelpText | null> | null;
};

interface CancellationProtectionProps {
  options?: CancellationProtectionOptions | null;
  limit: number;
  price: number | undefined | null;
  basePrice: number | undefined | null;
  fromPrice: number | undefined;
  per: string;
  boatCount: number;
  link?: string | null;
  onAdd: () => void;
  loading: boolean;
  errorMessage: string;
  infoMessage: string;
  isVisuallyDisabled: boolean;
  isApplied: boolean;
}

const CancellationProtection = (props: CancellationProtectionProps) => {
  const { language } = useLocale();
  const localize = useTypeSafeLocalize();
  const theme = useTheme();
  const formatPrice = useFormatPrice();

  const heading =
    props.options?.warrantyHelpTextHeading?.find(
      (translation) => translation?.lang === language
    )?.value ||
    localize(
      LocalizationCategory.product,
      ProductLocalization.cancellationWarrantyHeading
    );
  const body =
    props.options?.warrantyHelpTextBody?.find(
      (translation) => translation?.lang === language
    )?.value ||
    `${localize(
      LocalizationCategory.product,
      ProductLocalization.cancellationWarrantyText1
    )}${props.limit}${localize(
      LocalizationCategory.product,
      ProductLocalization.cancellationWarrantyText2
    )}`;

  return (
    <Box id="cancellation-protection">
      <VStack mb="3">
        <Heading2 color={theme.colors.cancellationWarranty.headingColor}>
          {heading}
        </Heading2>
      </VStack>

      <VStack mb="3">
        <Text renderWhiteSpace color={theme.colors.cancellationWarranty.textColor}>
          {body}
        </Text>

        {props.link && (
          <Link
            href={props.link}
            target="_blank"
            color={theme.colors.cancellationWarranty.textColor}
            mt="2"
          >
            {localize(LocalizationCategory.product, ProductLocalization.learnMore)}
          </Link>
        )}
      </VStack>

      <VStack>
        <HStack style={{ gap: '32px', justifyContent: 'space-between' }}>
          <VStack style={{ width: 'auto' }}>
            {props.price ? (
              <Text
                color={theme.colors.cancellationWarranty.textColor}
                style={{ whiteSpace: 'nowrap' }}
              >
                {localize(LocalizationCategory.price, PriceLocalization.price)}:{' '}
                {formatPrice(props.price)}
              </Text>
            ) : props.fromPrice ? (
              <Text
                color={theme.colors.cancellationWarranty.textColor}
                style={{ whiteSpace: 'nowrap' }}
              >
                {localize(LocalizationCategory.product, ProductLocalization.from)}:{' '}
                {formatPrice(props.fromPrice)}
              </Text>
            ) : null}

            {props.basePrice && props.per === 'boat' && (
              <SubLabel style={{ whiteSpace: 'nowrap' }}>
                {formatPrice(props.basePrice)} × {props.boatCount}{' '}
                {props.boatCount > 1
                  ? localize(LocalizationCategory.product, ProductLocalization.boats)
                  : localize(LocalizationCategory.product, ProductLocalization.boat)}
              </SubLabel>
            )}
          </VStack>

          <CPButton
            id="addCancellationProtectionButton"
            onClick={props.onAdd}
            variableWidth
            loading={props.loading}
            backgroundColor={theme.colors.cancellationWarranty.buttonBackgroundColor}
            color={theme.colors.cancellationWarranty.buttonTextColor}
            className={props.isVisuallyDisabled && 'disabled'}
            selected={props.isApplied}
          >
            {props.isApplied
              ? `${localize(LocalizationCategory.product, ProductLocalization.added)} ✓`
              : localize(LocalizationCategory.product, ProductLocalization.addToOrder)}
          </CPButton>
        </HStack>
      </VStack>

      {props.errorMessage !== '' ? (
        <VStack mt="3">
          <Label color={theme.colors.cancellationWarranty.headingColor}>
            {props.errorMessage}
          </Label>
        </VStack>
      ) : null}

      {props.errorMessage === '' && props.infoMessage !== '' ? (
        <VStack mt="3">
          <Label color={theme.colors.cancellationWarranty.textColor}>
            {props.infoMessage}
          </Label>
        </VStack>
      ) : null}
    </Box>
  );
};

export default CancellationProtection;
